import React, { useState, useEffect } from "react";
import { 
  Box, 
  TextField, 
  Button, 
  Typography, 
  Grid, 
  Paper, 
  Divider, 
  Tooltip,
  CircularProgress,
  Alert,
  Snackbar,
  IconButton,
  Card,
  CardMedia,
  Chip
} from "@mui/material";
import axios from "axios";
import CloseIcon from "@mui/icons-material/Close";
import EditIcon from "@mui/icons-material/Edit";
import SaveIcon from "@mui/icons-material/Save";
import CancelIcon from "@mui/icons-material/Cancel";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";


const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000';

const CompanyDetailsView = () => {
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [companyLogoPreview, setCompanyLogoPreview] = useState(null);
  const [newLogoFile, setNewLogoFile] = useState(null);

  const [accounts, setAccounts] = useState([{ accountNumber: "", purpose: "", bankname: "", ifsc: "" }]);

  const [companyData, setCompanyData] = useState({
    registration_no: "",
    companyname: "",
    country: "",
    currency_code: "",
    currency_symbol: "",
    timezone: "",
    contactemail: "",
    contactNumber: "",
    companyLogo: null,
    hq: "",
    address: "",
    branchLocation: "",
    tan: "",
    pfTrustName: "",
    pfRegno: "",
    pfAddress: "",
    CIN: "",
    PAN: "",
    GST: "",
    SGST: "",
    CGST:"",
    ESI: "",
    nationalIdLabel: "",
    bankCodeLabel: "",
    taxLabel: ""
  });

  const [originalData, setOriginalData] = useState(null);

  const [errors, setErrors] = useState({});

  // Get company_id from token
  const getCompanyIdFromToken = () => {
    const token = localStorage.getItem('token');
    if (!token) return null;
    
    try {
      const payload = JSON.parse(atob(token.split('.')[1]));
      return payload.company_id;
    } catch (error) {
      //console.error("Error parsing token:", error);
      return null;
    }
  };

  // Fetch company details
  const fetchCompanyDetails = async () => {
    try {
      setLoading(true);
      const companyId = getCompanyIdFromToken();
      
      if (!companyId) {
        setSnackbar({
          open: true,
          message: "No company ID found in token",
          severity: "error"
        });
        setLoading(false);
        return;
      }

      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_URL}/api/cmp/${companyId}`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.data.success) {
        const company = response.data.data;
        setCompanyData({
          registration_no: company.registration_no || "",
          companyname: company.companyname || "",
          country: company.country || "",
          currency_code: company.currency_code || "",
          currency_symbol: company.currency_symbol || "",
          timezone: company.timezone || "",
          contactemail: company.contactemail || "",
          contactNumber: company.contactNumber || "",
          companyLogo: company.companyLogo || null,
          hq: company.hq || "",
          address: company.address || "",
          branchLocation: company.branchLocation || "",
          tan: company.tan || "",
          pfTrustName: company.pfTrustName || "",
          pfRegno: company.pfRegno || "",
          pfAddress: company.pfAddress || "",
          CIN: company.CIN || "",
          PAN: company.PAN || "",
          GST: company.GST || "",
          SGST: company.SGST || "",
          CGST: company.CGST || "",
          ESI: company.ESI || "",
          nationalIdLabel: company.nationalIdLabel || "Company Registration No",
          bankCodeLabel: company.bankCodeLabel || "",
          taxLabel: company.taxLabel || "Tax ID"
        });

        // Set accounts
        if (company.accounts && Array.isArray(company.accounts)) {
          setAccounts(company.accounts);
        }

        // Set original data for cancel
        setOriginalData({
          ...company,
          accounts: company.accounts || []
        });

        // Set logo preview
        if (company.companyLogo) {
          setCompanyLogoPreview(company.companyLogo.startsWith('http') ? company.companyLogo : `${API_URL}/uploads/company-logos/${company.companyLogo}`);
        }
      }
    } catch (error) {
      //console.error("Error fetching company details:", error);
      setSnackbar({
        open: true,
        message: error.response?.data?.message || "Failed to fetch company details",
        severity: "error"
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchCompanyDetails();
  }, []);

  // Account handlers
  const handleAccountChange = (e, index) => {
    const { name, value } = e.target;
    const newAccounts = [...accounts];
    newAccounts[index][name] = value;
    setAccounts(newAccounts);
  };
  
  const addAccount = () => {
    setAccounts([...accounts, { accountNumber: "", purpose: "", bankname: "", ifsc: "" }]);
  };
  
  const removeAccount = (index) => {
    const newAccounts = accounts.filter((_, i) => i !== index);
    setAccounts(newAccounts);
  };

  // Validation
  const validate = () => {
    const newErrors = {};
    const isIndia = !companyData.country || companyData.country.toLowerCase() === "india";

    if (!companyData.companyname) newErrors.companyname = "Company name is required.";
    
    if (!companyData.CIN) {
      newErrors.CIN = `${companyData.nationalIdLabel || "Company Registration No"} is required.`;
    }
    
    if (isIndia) {
      if (!companyData.PAN) {
        newErrors.PAN = "PAN is required.";
      } else if (!/^[A-Z]{5}[0-9]{4}[A-Z]{1}$/.test(companyData.PAN)) {
        newErrors.PAN = "Invalid PAN format. (e.g., ABCDE1234F)";
      }   
      
      if (!companyData.GST) {
        newErrors.GST = "GST is required.";
      } else if (!/^[0-9]{2}[A-Z]{5}[0-9]{4}[A-Z]{1}[1-9A-Z]{1}[Z]{1}[0-9A-Z]{1}$/.test(companyData.GST)) {
        newErrors.GST = "Invalid GST format. (e.g., 27ABCDE1234F1Z5)";
      }   
      
      if (!companyData.SGST) {
        newErrors.SGST = "SGST is required.";
      } else if (!/^\d+(\.\d{1,2})?%?$/.test(companyData.SGST)) {
        newErrors.SGST = "Invalid SGST. Use a percentage (e.g., 9%) or numeric value.";
      }    

      if (!companyData.CGST) {
        newErrors.CGST = "CGST is required.";
      } else if (!/^\d+(\.\d{1,2})?%?$/.test(companyData.CGST)) {
        newErrors.CGST = "Invalid CGST. Use a percentage (e.g., 9%) or numeric value.";
      }  

      if (!companyData.tan) {
        newErrors.tan = "TAN is required (eg:ABCD09876H).";
      } else if (!/^[A-Za-z]{4}\d{5}[A-Za-z]{1}$/.test(companyData.tan)) {
        newErrors.tan = "TAN should be alphanumeric with 10 characters.";
      }
    } else {
      // Non-India: GST field acts as general Tax / VAT registration number
      if (!companyData.GST) {
        newErrors.GST = `${companyData.taxLabel} Registration Number is required.`;
      }
    }

    if (!companyData.contactemail) {
      newErrors.contactemail = "Email is required.";
    } else if (!/^[\w-]+(\.[\w-]+)*@([\w-]+\.)+[a-zA-Z]{2,7}$/.test(companyData.contactemail)) {
      newErrors.contactemail = "Enter a valid email.";
    }

    if (!companyData.contactNumber) {
      newErrors.contactNumber = "Contact number is required.";
    } else if (!/^\d{7,15}$/.test(companyData.contactNumber.replace(/[\s-]/g, ''))) {
      newErrors.contactNumber = "Please enter a valid contact number (7-15 digits).";
    }

    if (!companyData.address) newErrors.address = "Address is required.";
    if (!companyData.branchLocation) newErrors.branchLocation = "Branch Location is required.";
    if (!companyData.hq) newErrors.hq = "Head quarters is required.";
    
    accounts.forEach((account, index) => {
      if (!account.accountNumber) {
        newErrors[`accountNumber_${index}`] = `Account number is required for account ${index + 1}`;
      } else if (!/^[A-Za-z0-9\s-]{6,34}$/.test(account.accountNumber)) {
        newErrors[`accountNumber_${index}`] = `Invalid account number for account ${index + 1}`;
      }

      if (!account.purpose) {
        newErrors[`purpose_${index}`] = `Purpose is required for account ${index + 1}`;
      }

      if (!account.ifsc) {
        newErrors[`ifsc_${index}`] = `${companyData.bankCodeLabel} is required for account ${index + 1}.`;
      } else if (isIndia && !/^[A-Za-z]{4}\d{7}$/.test(account.ifsc)) {
        newErrors[`ifsc_${index}`] = `IFSC code must be 4 letters followed by 7 digits for account ${index + 1}`;
      } else if (!isIndia && account.ifsc.length < 3) {
        newErrors[`ifsc_${index}`] = `Please enter a valid ${companyData.bankCodeLabel} for account ${index + 1}`;
      }
      
      if (!account.bankname){
        newErrors[`bankname_${index}`] = `Bank name is required for account ${index + 1}`;
      }
    });

    if (editing && !companyData.companyLogo && !newLogoFile) {
      newErrors.companyLogo = "Company Logo is required";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleChange = (e) => {
    setCompanyData({ ...companyData, [e.target.name]: e.target.value });
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setNewLogoFile(file);
      const reader = new FileReader();
      reader.onloadend = () => {
        setCompanyLogoPreview(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleEdit = () => {
    setEditing(true);
  };

  const handleCancel = () => {
    if (originalData) {
      setCompanyData({
        registration_no: originalData.registration_no || "",
        companyname: originalData.companyname || "",
        contactemail: originalData.contactemail || "",
        contactNumber: originalData.contactNumber || "",
        companyLogo: originalData.companyLogo || null,
        hq: originalData.hq || "",
        address: originalData.address || "",
        branchLocation: originalData.branchLocation || "",
        tan: originalData.tan || "",
        pfTrustName: originalData.pfTrustName || "",
        pfRegno: originalData.pfRegno || "",
        pfAddress: originalData.pfAddress || "",
        CIN: originalData.CIN || "",
        PAN: originalData.PAN || "",
        GST: originalData.GST || "",
        SGST: originalData.SGST || "",
        CGST: originalData.CGST || "",
        ESI: originalData.ESI || ""
      });
      setAccounts(originalData.accounts || []);
      setNewLogoFile(null);
      if (originalData.companyLogo) {
        setCompanyLogoPreview(originalData.companyLogo.startsWith('http') ? originalData.companyLogo : `${API_URL}/uploads/company-logos/${originalData.companyLogo}`);
      } else {
        setCompanyLogoPreview(null);
      }
    }
    setEditing(false);
    setErrors({});
  };

  const handleUpdate = async (e) => {
    e.preventDefault();
    if (validate()) {
      try {
        const companyId = getCompanyIdFromToken();
        if (!companyId) {
          setSnackbar({
            open: true,
            message: "No company ID found in token",
            severity: "error"
          });
          return;
        }

        const token = localStorage.getItem('token');
        const formData = new FormData();

        // Append all company data
        Object.keys(companyData).forEach((key) => {
          if (key !== 'companyLogo' && key !== 'registration_no') {
            formData.append(key, companyData[key]);
          }
        });

        // Append accounts as JSON string
        formData.append("accounts", JSON.stringify(accounts));

        // Append new logo file if selected
        if (newLogoFile) {
          formData.append("companyLogo", newLogoFile);
        }

        const url = `${API_URL}/api/cmp/${companyId}`;

        const response = await axios.put(url, formData, {
          headers: {
            "Authorization": `Bearer ${token}`,
            "Content-Type": "multipart/form-data",
          },
        });

        if (response.data.success) {
          setSnackbar({
            open: true,
            message: "Company updated successfully!",
            severity: "success"
          });
          
          // Refresh data
          fetchCompanyDetails();
          setEditing(false);
          setNewLogoFile(null);
        }

      } catch (error) {
        //console.error("Error updating company:", error);
        setSnackbar({
          open: true,
          message: error.response?.data?.message || "Failed to update company",
          severity: "error"
        });
      }
    }
  };

  const handleSnackbarClose = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '400px' }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ maxWidth: 1500, margin: 'auto', padding: 3 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4" gutterBottom align="left" fontWeight="bold">
          Company Details
        </Typography>
        
        {!editing ? (
          <Button 
            variant="contained" 
            startIcon={<EditIcon />}
            onClick={handleEdit}
            color="primary"
          >
            Edit Details
          </Button>
        ) : (
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Button 
              variant="contained" 
              startIcon={<SaveIcon />}
              onClick={handleUpdate}
              color="success"
            >
              Save Changes
            </Button>
            <Button 
              variant="outlined" 
              startIcon={<CancelIcon />}
              onClick={handleCancel}
              color="error"
            >
              Cancel
            </Button>
          </Box>
        )}
      </Box>

      <Paper elevation={3} sx={{ padding: 3, marginBottom: 3 }}>
        <Typography variant="h6" gutterBottom>
          Company Information
        </Typography>
        <Divider sx={{ marginBottom: 2 }} />

        <Grid container spacing={2}>
          {/* Company Logo */}
          <Grid item xs={12} md={4}>
            <Box sx={{ mb: 2 }}>
              <Typography variant="subtitle1" gutterBottom>
                Company Logo
              </Typography>
              {companyLogoPreview ? (
                <Card sx={{ maxWidth: 200 }}>
                  <CardMedia
                    component="img"
                    height="200"
                    image={companyLogoPreview}
                    alt="Company Logo"
                  />
                </Card>
              ) : (
                <Chip label="No Logo Uploaded" color="warning" variant="outlined" />
              )}
              
              {editing && (
                <Box sx={{ mt: 2 }}>
                  <input
                    type="file"
                    accept="image/*"
                    id="logo-upload"
                    style={{ display: 'none' }}
                    onChange={handleFileChange}
                  />
                  <label htmlFor="logo-upload">
                    <Button
                      variant="outlined"
                      component="span"
                      startIcon={<CloudUploadIcon />}
                      fullWidth
                    >
                      {companyLogoPreview ? "Change Logo" : "Upload Logo"}
                    </Button>
                  </label>
                  {errors.companyLogo && (
                    <Typography variant="caption" color="error" sx={{ mt: 1, display: 'block' }}>
                      {errors.companyLogo}
                    </Typography>
                  )}
                </Box>
              )}
            </Box>
          </Grid>

          {/* Company Details */}
          <Grid item xs={12} md={8}>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <Tooltip title="Company Name" placement="top" arrow>
                  <TextField
                    label="Company Name"
                    name="companyname"
                    value={companyData.companyname}
                    onChange={handleChange}
                    error={!!errors.companyname}
                    helperText={errors.companyname}
                    fullWidth
                    required
                    disabled={!editing}
                  />
                </Tooltip>
              </Grid>

              <Grid item xs={12} sm={6}>
                <Tooltip title="Company Registration Number" placement="top" arrow>
                  <TextField
                    label="Registration Number"
                    name="registration_no"
                    value={companyData.registration_no}
                    fullWidth
                    disabled
                    InputProps={{
                      readOnly: true,
                    }}
                  />
                </Tooltip>
              </Grid>

              <Grid item xs={12} sm={6}>
                <Tooltip title="Registered Country" placement="top" arrow>
                  <TextField
                    label="Country"
                    name="country"
                    value={companyData.country || "India"}
                    fullWidth
                    disabled
                    InputProps={{
                      readOnly: true,
                    }}
                  />
                </Tooltip>
              </Grid>

              <Grid item xs={12} sm={6}>
                <Tooltip title="Company Base Currency" placement="top" arrow>
                  <TextField
                    label="Base Currency"
                    name="currency_code"
                    value={`${companyData.currency_code } (${companyData.currency_symbol })`}
                    fullWidth
                    disabled
                    InputProps={{
                      readOnly: true,
                    }}
                  />
                </Tooltip>
              </Grid>

              <Grid item xs={12} sm={6}>
                <Tooltip title="Contact Email" placement="top" arrow>
                  <TextField
                    label="Contact Email"
                    name="contactemail"
                    value={companyData.contactemail}
                    onChange={handleChange}
                    error={!!errors.contactemail}
                    helperText={errors.contactemail}
                    fullWidth
                    required
                    disabled={!editing}
                  />
                </Tooltip>
              </Grid>

              <Grid item xs={12} sm={6}>
                <Tooltip title="Contact Number" placement="top" arrow>
                  <TextField
                    label="Contact Number"
                    name="contactNumber"
                    value={companyData.contactNumber}
                    onChange={handleChange}
                    error={!!errors.contactNumber}
                    helperText={errors.contactNumber}
                    fullWidth
                    required
                    disabled={!editing}
                  />
                </Tooltip>
              </Grid>

              <Grid item xs={12} sm={6}>
                <Tooltip title="Headquarters" placement="top" arrow>
                  <TextField
                    label="Headquarters"
                    name="hq"
                    value={companyData.hq}
                    onChange={handleChange}
                    error={!!errors.hq}
                    helperText={errors.hq}
                    fullWidth
                    required
                    disabled={!editing}
                  />
                </Tooltip>
              </Grid>

              <Grid item xs={12} sm={6}>
                <Tooltip title="Branch Location" placement="top" arrow>
                  <TextField
                    label="Branch Location"
                    name="branchLocation"
                    value={companyData.branchLocation}
                    onChange={handleChange}
                    error={!!errors.branchLocation}
                    helperText={errors.branchLocation}
                    fullWidth
                    required
                    disabled={!editing}
                  />
                </Tooltip>
              </Grid>

              <Grid item xs={12}>
                <Tooltip title="Company Address" placement="top" arrow>
                  <TextField
                    label="Address"
                    name="address"
                    value={companyData.address}
                    onChange={handleChange}
                    error={!!errors.address}
                    helperText={errors.address}
                    fullWidth
                    required
                    multiline
                    rows={2}
                    disabled={!editing}
                  />
                </Tooltip>
              </Grid>
            </Grid>
          </Grid>
        </Grid>
      </Paper>

      {/* Account Details */}
      <Paper elevation={3} sx={{ padding: 3, marginBottom: 3 }}>
        <Typography variant="h6" gutterBottom>
          Account Details
        </Typography>
        <Divider sx={{ marginBottom: 2 }} />

        {accounts.map((acc, index) => (
          <Grid container spacing={2} key={index} alignItems="center">
            <Grid item xs={12} sm={3}>
              <Tooltip title="Account Number" placement="top" arrow>
                <TextField
                  margin="normal"
                  label="Account Number"
                  name="accountNumber"
                  value={acc.accountNumber}
                  error={!!errors[`accountNumber_${index}`]}
                  helperText={errors[`accountNumber_${index}`]}
                  onChange={(e) => handleAccountChange(e, index)}
                  fullWidth
                  required
                  disabled={!editing}
                />
              </Tooltip>
            </Grid>

            <Grid item xs={12} sm={3}>
              <Tooltip title="Purpose" placement="top" arrow>
                <TextField
                  margin="normal"
                  label="Purpose"
                  name="purpose"
                  value={acc.purpose}
                  error={!!errors[`purpose_${index}`]}
                  helperText={errors[`purpose_${index}`]}
                  onChange={(e) => handleAccountChange(e, index)}
                  fullWidth
                  required
                  disabled={!editing}
                />
              </Tooltip>
            </Grid>

            <Grid item xs={12} sm={3}>
              <Tooltip title="Bank Name" placement="top" arrow>
                <TextField
                  margin="normal"
                  label="Bank Name"
                  name="bankname"
                  value={acc.bankname}
                  error={!!errors[`bankname_${index}`]}
                  helperText={errors[`bankname_${index}`]}
                  onChange={(e) => handleAccountChange(e, index)}
                  fullWidth
                  required
                  disabled={!editing}
                />
              </Tooltip>
            </Grid>

            <Grid item xs={12} sm={2}>
              <Tooltip title={companyData.bankCodeLabel || 'Bank Code'} placement="top" arrow>
                <TextField
                  margin="normal"
                  label={companyData.bankCodeLabel || 'Bank Code'}
                  placeholder={`Enter ${companyData.bankCodeLabel || 'Bank Code'}`}
                  name="ifsc"
                  value={acc.ifsc}
                  error={!!errors[`ifsc_${index}`]}
                  helperText={errors[`ifsc_${index}`]}
                  onChange={(e) => handleAccountChange(e, index)}
                  fullWidth
                  required
                  disabled={!editing}
                />
              </Tooltip>
            </Grid>

            {editing && index > 0 && (
              <Grid item xs={12} sm={1}>
                <IconButton
                  sx={{ mt: 2 }}
                  onClick={() => removeAccount(index)}
                  color="error"
                >
                  <CloseIcon />
                </IconButton>
              </Grid>
            )}
          </Grid>
        ))}

        {editing && (
          <Box sx={{ display: "flex", justifyContent: "flex-start", mt: 2 }}>
            <Button variant="outlined" onClick={addAccount}>
              Add Another Account
            </Button>
          </Box>
        )}
      </Paper>

      {/* Tax & Regulatory Details */}
      <Paper elevation={3} sx={{ padding: 3, marginBottom: 3 }}>
        <Typography variant="h6" gutterBottom>
          Tax & Regulatory Details ({companyData.country || "India"})
        </Typography>
        <Divider sx={{ marginBottom: 2 }} />

        <Grid container spacing={2}>
          <Grid item xs={12} sm={6} md={4}>
            <Tooltip title={`Enter ${companyData.nationalIdLabel || 'CIN/Registration No'}`} placement="top" arrow>
              <TextField
                label={companyData.nationalIdLabel || 'CIN/Registration No'}
                placeholder={`Enter ${companyData.nationalIdLabel || 'CIN/Registration No'}`}
                name="CIN"
                value={companyData.CIN}
                onChange={handleChange}
                error={!!errors.CIN}
                helperText={errors.CIN}
                fullWidth
                required
                disabled={!editing}
              />
            </Tooltip>
          </Grid>

          <Grid item xs={12} sm={6} md={4}>
            <Tooltip title={`Enter ${companyData.taxLabel || 'Tax ID'}`} placement="top" arrow>
              <TextField
                label={(companyData.taxLabel || 'Tax ID').includes("IVA") || (companyData.taxLabel || 'Tax ID').includes("Number") 
                  ? (companyData.taxLabel || 'Tax ID') 
                  : `${companyData.taxLabel || 'Tax ID'} Registration Number`}
                name="GST"
                value={companyData.GST}
                onChange={handleChange}
                error={!!errors.GST}
                helperText={errors.GST}
                fullWidth
                required
                disabled={!editing}
              />
            </Tooltip>
          </Grid>

          {(!companyData.country || companyData.country.toLowerCase() === "india") && (
            <>
              <Grid item xs={12} sm={6} md={4}>
                <Tooltip title="PAN (Permanent Account Number)" placement="top" arrow>
                  <TextField
                    label="PAN"
                    name="PAN"
                    value={companyData.PAN}
                    onChange={handleChange}
                    error={!!errors.PAN}
                    helperText={errors.PAN}
                    fullWidth
                    required
                    disabled={!editing}
                  />
                </Tooltip>
              </Grid>

              <Grid item xs={12} sm={6} md={4}>
                <Tooltip title="TAN (Tax Deduction and Collection Account Number)" placement="top" arrow>
                  <TextField
                    label="TAN"
                    name="tan"
                    value={companyData.tan}
                    onChange={handleChange}
                    error={!!errors.tan}
                    helperText={errors.tan}
                    fullWidth
                    required
                    disabled={!editing}
                  />
                </Tooltip>
              </Grid>

              <Grid item xs={12} sm={6} md={4}>
                <Tooltip title="SGST (State Goods and Services Tax)" placement="top" arrow>
                  <TextField
                    label="SGST"
                    name="SGST"
                    value={companyData.SGST}
                    onChange={handleChange}
                    error={!!errors.SGST}
                    helperText={errors.SGST}
                    fullWidth
                    required
                    disabled={!editing}
                  />
                </Tooltip>
              </Grid>

              <Grid item xs={12} sm={6} md={4}>
                <Tooltip title="CGST (Central Goods and Services Tax)" placement="top" arrow>
                  <TextField
                    label="CGST"
                    name="CGST"
                    value={companyData.CGST}
                    onChange={handleChange}
                    error={!!errors.CGST}
                    helperText={errors.CGST}
                    fullWidth
                    required
                    disabled={!editing}
                  />
                </Tooltip>
              </Grid>
            </>
          )}

          <Grid item xs={12} sm={6} md={4}>
            <Tooltip title="ESI (Employees' State Insurance)" placement="top" arrow>
              <TextField
                label="ESI"
                name="ESI"
                value={companyData.ESI}
                onChange={handleChange}
                fullWidth
                disabled={!editing}
              />
            </Tooltip>
          </Grid>
        </Grid>
      </Paper>

      {/* PF/Trust Details */}
      {(!companyData.country || companyData.country.toLowerCase() === "india") && (
        <Paper elevation={3} sx={{ padding: 3, marginBottom: 3 }}>
          <Typography variant="h6" gutterBottom>
            PF/Trust Details (if applicable)
          </Typography>
          <Divider sx={{ marginBottom: 2 }} />

          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <Tooltip title="PF Trust Name" placement="top" arrow>
                <TextField
                  label="Trust Name"
                  name="pfTrustName"
                  value={companyData.pfTrustName || ""}
                  onChange={handleChange}
                  fullWidth
                  disabled={!editing}
                />
              </Tooltip>
            </Grid>

            <Grid item xs={12} sm={6}>
              <Tooltip title="PF Registration Number" placement="top" arrow>
                <TextField
                  label="Registration Number"
                  name="pfRegno"
                  value={companyData.pfRegno || ""}
                  onChange={handleChange}
                  fullWidth
                  disabled={!editing}
                />
              </Tooltip>
            </Grid>

            <Grid item xs={12}>
              <Tooltip title="PF Trust Address" placement="top" arrow>
                <TextField
                  label="Trust Address"
                  name="pfAddress"
                  value={companyData.pfAddress || ""}
                  onChange={handleChange}
                  fullWidth
                  multiline
                  rows={2}
                  disabled={!editing}
                />
              </Tooltip>
            </Grid>
          </Grid>
        </Paper>
      )}

      {/* Snackbar for notifications */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleSnackbarClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert onClose={handleSnackbarClose} severity={snackbar.severity} sx={{ width: '100%' }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default CompanyDetailsView;